import '../App.css';
import {
    Box, Button,
    createTheme, CssBaseline,
    Stack, TextField,
    ThemeProvider,
    Typography,
    useMediaQuery,
    CircularProgress,
} from "@mui/material";
import {Download} from "@mui/icons-material";
import {useQrStyleForm} from "../Componant/QrCode/QrStyleForm";
import {useEffect, useState} from "react";
import axios from "axios";
import SideBar from "../Componant/sidebar/Sidebar";
import {isValidUrl} from "../Utils/urlUtils";

function QrGeneratorPage() {

    const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
    const isComputer = useMediaQuery('(min-width:900px)');
    const [sidebarIsOpen, setSidebarIsOpen] = useState();
    const [url, setUrl] = useState('');
    const [style, setStyle] = useState({});

    const [previewImage, setPreviewImage] = useState(null);
    const [previewFormat, setPreviewFormat] = useState('png');
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState(null);

    const {isDesktop, basicFields, advancedFields, mobileNav} = useQrStyleForm({}, setStyle);

    const darkTheme = createTheme({
        palette: {
            mode: prefersDarkMode ? 'dark' : 'light',

            background: {
                default: prefersDarkMode ? '#1A1A1A' : '#f5f5f5',
            },

            common: {
                white: {
                    dark: '#cecece'
                },
            },

            greyTimeline: {
                main: '#121212',
            },

            appBackground: {
                main: '#f5f5f5',
                light: '#f5f5f5',
                dark: '#252525'
            },

            reactflowButton: {
                main: '#FFFFFF',
                light: '#FFFFFF',
                dark: '#252525'
            },

            sidebar: {
                dark: '#cecece',
                light: '#616161',
                main: '#616161'
            },

            nodeHandle: {
                dark: '#000000FF',
                light: '#FFFFFF',
                main: '#FFFFFF'
            },

            nodeCard: {
                dark: '#252525',
                light: '#FFFFFF',
                main: '#FFFFFF'
            },

            primary: {
                main: '#FF7233',
                light: '#FF7233',
                dark: '#e05a1a',
            }

        },
        typography: {
            fontFamily: '"Poppins",  sans-serif',
            allVariants: {
                color: prefersDarkMode ? '#cecece' : '#000000FF',
            }
        },
    });

    const urlTouched = url.trim().length > 0;
    const urlIsValid = isValidUrl(url);

    // Aperçu QR live : régénéré (avec un léger débounce) à chaque changement d'URL ou de style.
    useEffect(() => {
        if (!urlIsValid) {
            setPreviewImage(null);
            setPreviewError(null);
            setPreviewLoading(false);
            return;
        }

        setPreviewLoading(true);
        setPreviewError(null);

        const timer = setTimeout(async () => {
            try {
                const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3033';
                const response = await axios.post(`${apiUrl}/api/qrcodes/generate`, {url, ...style}, {
                    headers: {'Content-Type': 'application/json'}
                });

                if (!response.data.success) {
                    throw new Error(response.data.error || 'Erreur lors de la génération');
                }

                setPreviewImage(response.data.image);
                setPreviewFormat(response.data.format || style.format || 'png');
            } catch (error) {
                console.error('Erreur lors de la génération:', error);
                setPreviewImage(null);

                let errorMessage = 'Erreur lors de la génération du QR code';
                if (error.response?.status === 413) {
                    errorMessage = 'Le logo est trop volumineux. Veuillez choisir une image plus petite.';
                } else if (error.response?.data?.error) {
                    errorMessage = error.response.data.error;
                } else if (error.message) {
                    errorMessage = error.message;
                }
                setPreviewError(errorMessage);
            } finally {
                setPreviewLoading(false);
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [url, style, urlIsValid]);

    const handleDownload = () => {
        if (!previewImage) return;
        const link = document.createElement('a');
        link.href = previewImage;
        link.download = `qrcode.${previewFormat}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const getContainerClass = () => {
        let classes = []
        if (!isComputer) {
            classes.push("mobile")
        } else {
            const animation = sidebarIsOpen ? 'activated' : 'disactivated'
            classes.push(animation)
        }

        return classes.join(' ')
    }

    const urlField = (
        <Box width={'100%'}>
            <Typography variant={'h6'}>
                URL à encoder :
            </Typography>
            <TextField
                fullWidth
                variant='outlined'
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://exemple.com"
                error={urlTouched && !urlIsValid}
                helperText={urlTouched && !urlIsValid ? 'URL invalide (doit commencer par http:// ou https://)' : ' '}
            />
        </Box>
    );

    const previewBlock = (
        <Box width="100%">
            <Typography variant="h6" gutterBottom>Aperçu</Typography>
            <Box sx={{
                width: '100%', maxWidth: isDesktop ? 'none' : 380, mx: isDesktop ? 0 : 'auto',
                aspectRatio: '1 / 1',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '1px solid', borderColor: 'divider', borderRadius: 2,
                position: 'relative', overflow: 'hidden', p: 2,
            }}>
                {previewImage && (
                    <Box component="img" src={previewImage} alt="Aperçu du QR code"
                         sx={{width: '100%', height: '100%', objectFit: 'contain'}}/>
                )}
                {!previewImage && !previewLoading && previewError && (
                    <Typography variant="body2" color="error" textAlign="center" sx={{px: 2}}>
                        {previewError}
                    </Typography>
                )}
                {!previewImage && !previewLoading && !previewError && (
                    <Typography variant="body2" color="text.secondary" textAlign="center" sx={{px: 2}}>
                        {urlTouched && !urlIsValid
                            ? "L'URL saisie n'est pas valide"
                            : "Saisissez une URL pour voir l'aperçu"}
                    </Typography>
                )}
                {previewLoading && (
                    <Box sx={{
                        position: 'absolute', inset: 0, display: 'flex',
                        alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(0,0,0,0.15)',
                    }}>
                        <CircularProgress size={32}/>
                    </Box>
                )}
            </Box>

            <Button
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                startIcon={<Download/>}
                onClick={handleDownload}
                disabled={!previewImage}
                sx={{mt: 2}}
            >
                Télécharger
            </Button>
        </Box>
    );

    return (
        <div className="App">
            <ThemeProvider theme={darkTheme}>


                <SideBar sidebarIsOpen={sidebarIsOpen} setSidebarIsOpen={setSidebarIsOpen} isComputer={isComputer}/>
                <CssBaseline/>

                <Box paddingTop={'5%'} paddingBottom={'5%'} id='Md-Container'
                     className={getContainerClass()}>
                    <Box sx={{padding: 4, margin: '0 auto', width: '100%', maxWidth: 1400}}>
                        <Typography variant={'h4'} gutterBottom>
                            QR Code générateur
                        </Typography>
                        <Box sx={{pb: isDesktop ? undefined : 9}}>
                            {isDesktop ? (
                                <>
                                    <Stack direction="row" spacing={5} alignItems="flex-start">
                                        <Stack spacing={6} sx={{flex: '1 1 480px', maxWidth: 640, width: '100%'}}>
                                            {urlField}
                                            {basicFields}
                                        </Stack>

                                        <Box sx={{flex: '0 0 auto', width: 380, position: 'sticky', top: 24}}>
                                            {previewBlock}
                                        </Box>
                                    </Stack>

                                    <Box sx={{mt: 5}}>
                                        {advancedFields}
                                    </Box>
                                </>
                            ) : (
                                <>
                                    <Stack spacing={4}>
                                        {urlField}
                                        {previewBlock}
                                    </Stack>
                                    {mobileNav}
                                </>
                            )}
                        </Box>
                    </Box>
                </Box>
            </ThemeProvider>
        </div>
    );
}

export default QrGeneratorPage;
